import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    MessageBarProps,
    tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { customPalette } from '../../theme';

export interface IModalProps {
    isOpen: boolean;
    onClose: () => void;
    headerContent?: React.ReactNode;
    bodyContent?: React.ReactNode;
    buttons?: React.ReactNode;
    leftFooterElement?: React.ReactNode;
    messageBar?: React.ReactElement<MessageBarProps>;
    elementToFocusOnDismiss?: HTMLElement | undefined;
    showModalCloseButton?: boolean;
    isBlocking?: boolean;
    zIndex?: number;
    modalMaxWidthInPixels?: number;
    modalMaxHeightInPixels?: number;
    id?: string;
    contentStylesProps?: string;
    bodyStylesProps?: string;
    //TODO: do we need this? why not just render everything within the dialog content (bodyContent prop)?
    infoContent?: React.ReactNode;
}

const useStyles = makeStyles({
    dialogStyles: {
        borderRadius: tokens.borderRadiusXLarge,
        padding: '24px',
    },
    bodyStyles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
    },
    contentStyles: {
        lineHeight: tokens.lineHeightBase300,
        fontFamily: 'Segoe UI',
    },
    leftFooterStyles: { flex: 1, justifyItems: 'flex-start', alignContent: 'center' },
    actionStyles: { display: 'flex', gap: '10px' },
    titleContainerStyles: { display: 'grid' },
});

const ModalV9: React.FC<IModalProps> = ({
    isOpen,
    onClose,
    headerContent,
    bodyContent,
    buttons,
    leftFooterElement,
    messageBar,
    elementToFocusOnDismiss,
    showModalCloseButton = true,
    isBlocking,
    zIndex,
    modalMaxWidthInPixels,
    modalMaxHeightInPixels,
    id,
    contentStylesProps,
    bodyStylesProps,
    infoContent,
}) => {
    const styles = {
        content: {
            color: customPalette.blueDark,
        },
        dialog: {
            maxWidth: modalMaxWidthInPixels ? `${modalMaxWidthInPixels}px` : '600px',
            maxHeight: modalMaxHeightInPixels ? `${modalMaxHeightInPixels}px` : '729px',
            zIndex: zIndex,
        },
    };

    const { t } = useTranslation();

    const { dialogStyles, leftFooterStyles, contentStyles, bodyStyles, actionStyles, titleContainerStyles } = useStyles();

    useEffect(() => {
        if (!isOpen && elementToFocusOnDismiss) {
            elementToFocusOnDismiss.focus();
        }
    }, [isOpen, elementToFocusOnDismiss]);

    return (
        <>
            {isOpen && (
                <Dialog
                    modalType={isBlocking ? 'alert' : 'modal'}
                    open={isOpen}
                    onOpenChange={(_, data) => {
                        if (isBlocking && data.type === 'backdropClick') {
                            return;
                        }
                        if (!data.open) {
                            onClose();
                        }
                    }}
                >
                    <DialogSurface className={dialogStyles} style={styles.dialog} id={id}>
                        <DialogBody className={bodyStylesProps ? bodyStylesProps : bodyStyles}>
                            <div className={titleContainerStyles}>
                                <DialogTitle
                                    action={
                                        showModalCloseButton ? (
                                            <Button
                                                appearance='subtle'
                                                icon={<DismissRegular />}
                                                aria-label={t('closeModal').toString()}
                                                onClick={onClose}
                                            />
                                        ) : undefined
                                    }
                                >
                                    {headerContent}
                                </DialogTitle>
                            </div>

                            {messageBar}

                            <DialogContent
                                id='modalDescription'
                                className={contentStylesProps ? contentStylesProps : contentStyles}
                                style={styles.content}
                            >
                                {bodyContent}
                            </DialogContent>
                            {infoContent}
                            <DialogActions>
                                <div className={leftFooterStyles}>{leftFooterElement}</div>
                                <div className={actionStyles}>{buttons}</div>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </>
    );
};

export default ModalV9;
